import { lazy, Suspense } from "react";
import { SUPABASE_URL, SUPABASE_PROJECT_ID } from "@/integrations/supabase/env";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ShowsProvider } from "@/contexts/ShowsContext";
import { EpisodesProvider } from "@/contexts/EpisodesContext";
import { BugReportProvider } from "@/contexts/BugReportContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminProtectedRoute, PlatformAdminRoute, AnyAdminRoute, StudioAdminRoute, SchedulingAdminRoute, SchedulingSelfRoute } from "@/components/AdminProtectedRoute";
import { RecoveryRedirect } from "@/components/auth/RecoveryRedirect";
import { PodcasterLayout } from "@/components/layout/PodcasterLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { RouteTooltips } from "@/features/tooltips/RouteTooltips";

import { VersionUpdatePrompt } from "@/components/VersionUpdatePrompt";
import { StagingModeBanner } from "@/components/StagingModeBanner";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { SeoManager } from "@/components/seo/SeoManager";

// Eagerly load the homepage — it's the landing page
import Index from "./pages/Index";

// Lazy-load everything else
const Dashboard = lazy(() => import("@/pages/podcaster/Dashboard"));
const Shows = lazy(() => import("@/pages/podcaster/Shows"));
const ShowDetail = lazy(() => import("@/pages/podcaster/ShowDetail"));
const Episodes = lazy(() => import("@/pages/podcaster/Episodes"));
const EpisodeDetail = lazy(() => import("@/pages/podcaster/EpisodeDetail"));
const EpisodeUpload = lazy(() => import("@/pages/podcaster/EpisodeUpload"));
const DeliveryStatus = lazy(() => import("@/pages/podcaster/DeliveryStatus"));
const Analytics = lazy(() => import("@/pages/podcaster/Analytics"));
const AnalyticsV2 = lazy(() => import("@/pages/podcaster/AnalyticsV2"));
const Revenue = lazy(() => import("@/pages/podcaster/Revenue"));
const Wallet = lazy(() => import("@/pages/podcaster/Wallet"));
const Earnings = lazy(() => import("@/pages/podcaster/Earnings"));
const StripeConnectReturn = lazy(() => import("@/pages/podcaster/StripeConnectReturn"));
const RevenueSplit = lazy(() => import("@/pages/podcaster/RevenueSplit"));
const Billing = lazy(() => import("@/pages/podcaster/Billing"));
const Settings = lazy(() => import("@/pages/podcaster/Settings"));
const Team = lazy(() => import("@/pages/podcaster/Team"));

const Referrals = lazy(() => import("@/pages/podcaster/Referrals"));
const Help = lazy(() => import("@/pages/podcaster/Help"));
const Shop = lazy(() => import("@/pages/podcaster/Shop"));
const BookStudio = lazy(() => import("@/pages/podcaster/BookStudio"));
const MyBookings = lazy(() => import("@/pages/podcaster/MyBookings"));
const MyBookingDetail = lazy(() => import("@/pages/podcaster/MyBookingDetail"));

const Auth = lazy(() => import("@/pages/Auth"));
const ContractReturn = lazy(() => import("@/pages/ContractReturn"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const DMCA = lazy(() => import("@/pages/DMCA"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const GDPRPage = lazy(() => import("@/pages/GDPR"));
const PlanSelection = lazy(() => import("@/pages/onboarding/PlanSelection"));
const TeamInvite = lazy(() => import("@/pages/onboarding/TeamInvite"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const RedeemInvite = lazy(() => import("@/pages/RedeemInvite"));
const SessionExpired = lazy(() => import("./pages/SessionExpired"));
const NotFound = lazy(() => import("./pages/NotFound"));

// TEMP: demo-only onboarding prototype, remove after demo
const OnboardingDemo = () => {
  if (typeof window !== "undefined") {
    window.location.replace("/onboarding-demo/index.html");
  }
  return null;
};

const ClipStudio = lazy(() => import("./pages/ClipStudio"));
const VoiceStudio = lazy(() => import("./pages/VoiceStudio"));



const PublicShowPage = lazy(() => import("./pages/public/ShowPage"));
const SmartLinkRedirect = lazy(() => import("./pages/SmartLinkRedirect"));
const PublicHelp = lazy(() => import("./pages/public/PublicHelp"));
const BlogFeed = lazy(() => import("./pages/public/BlogFeed"));
const BlogArticle = lazy(() => import("./pages/public/BlogArticle"));
const Studios = lazy(() => import("./pages/public/Studios"));
const StudioLocationPage = lazy(() => import("./pages/public/StudioLocationPage"));
const StudioRoomPage = lazy(() => import("./pages/public/StudioRoomPage"));
const StudioReservationThankYou = lazy(() => import("./pages/public/StudioReservationThankYou"));
const StudioBookingActionResult = lazy(() => import("./pages/public/StudioBookingActionResult"));
const Careers = lazy(() => import("./pages/public/Careers"));
const About = lazy(() => import("./pages/public/About"));
const WhyJoinPodgo = lazy(() => import("./pages/public/WhyJoinPodgo"));

const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const StudioAdminDashboard = lazy(() => import("@/pages/admin/StudioAdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminUserDetail = lazy(() => import("@/pages/admin/UserDetail"));
const AdminPodcasts = lazy(() => import("@/pages/admin/Podcasts"));
const AdminTrashedEpisodes = lazy(() => import("@/pages/admin/TrashedEpisodes"));
const AdminSupport = lazy(() => import("@/pages/admin/Support"));
const AdminPlatformAnalytics = lazy(() => import("@/pages/admin/PlatformAnalytics"));
const AdminPlatformRevenue = lazy(() => import("@/pages/admin/insights/PlatformRevenue"));
const EmailTemplates = lazy(() => import("@/pages/admin/EmailTemplates"));
const SMSTemplates = lazy(() => import("@/pages/admin/SMSTemplates"));
const Templates = lazy(() => import("@/pages/admin/Templates"));
const AdminSegments = lazy(() => import("@/pages/admin/Segments"));
const AdminCampaigns = lazy(() => import("@/pages/admin/Campaigns"));
const AdminCampaignEditor = lazy(() => import("@/pages/admin/CampaignEditor"));
const AdminCampaignStats = lazy(() => import("@/pages/admin/CampaignStats"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const ApprovalQueue = lazy(() => import("@/pages/admin/ApprovalQueue"));
const ApprovalReview = lazy(() => import("@/pages/admin/ApprovalReview"));
const AdminExpenses = lazy(() => import("@/pages/admin/Expenses"));
const AdminRevenueEntries = lazy(() => import("@/pages/admin/RevenueEntries"));
const AdminWithdrawals = lazy(() => import("@/pages/admin/Withdrawals"));
const AdminNews = lazy(() => import("./pages/admin/News"));
const AdminArticleEdit = lazy(() => import("./pages/admin/ArticleEdit"));
const AdminStudios = lazy(() => import("./pages/admin/Studios"));
const StudioLocationDetail = lazy(() => import("./pages/admin/StudioLocationDetail"));
const StudioRoomDetail = lazy(() => import("./pages/admin/StudioRoomDetail"));
const StudioBookings = lazy(() => import("./pages/admin/StudioBookings"));
const StudioBookingDetail = lazy(() => import("./pages/admin/StudioBookingDetail"));
const AdminCareers = lazy(() => import("./pages/admin/Careers"));
const JobPostingEdit = lazy(() => import("./pages/admin/JobPostingEdit"));
const JobApplications = lazy(() => import("./pages/admin/JobApplications"));
const AdminBugReports = lazy(() => import("./pages/admin/BugReports"));
const SchedulingOverview = lazy(() => import("@/pages/admin/scheduling/SchedulingOverview"));
const SchedulingGroups = lazy(() => import("@/pages/admin/scheduling/SchedulingGroups"));
const SchedulingBookingTypes = lazy(() => import("@/pages/admin/scheduling/SchedulingBookingTypes"));
const MyAvailability = lazy(() => import("@/pages/admin/scheduling/MyAvailability"));
const AdminThemeOptions = lazy(() => import("./pages/admin/ThemeOptions"));
const AdminLinkHub = lazy(() => import("./pages/admin/LinkHub"));
const AdminSyncExclusions = lazy(() => import("./pages/admin/SyncExclusions"));
const AdminFailedSyncs = lazy(() => import("./pages/admin/FailedSyncs"));
const AdminNetworkAdmins = lazy(() => import("./pages/admin/NetworkAdmins"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const ProductionPreviewIndicator = () => {
  const isStandalonePreviewTarget = import.meta.env.VITE_PODGO_BACKEND_TARGET === "standalone-preview";
  const isPreviewOrDevHost = typeof window !== "undefined" && ["lovable.app", "localhost", "127.0.0.1"].some((host) => {
    const hostname = window.location.hostname;
    return hostname === host || hostname.endsWith(`.${host}`);
  });
  const backendHost = (() => {
    try {
      return new URL(SUPABASE_URL).hostname;
    } catch {
      return SUPABASE_PROJECT_ID || "unknown backend";
    }
  })();

  if (!isStandalonePreviewTarget || !isPreviewOrDevHost) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-[min(20rem,calc(100vw-2rem))] rounded-md border border-destructive/30 bg-destructive px-3 py-2 text-xs font-semibold tracking-normal text-destructive-foreground shadow-lg">
      <div className="uppercase">Standalone preview backend</div>
      <div className="mt-0.5 truncate normal-case opacity-90">{backendHost}</div>
    </div>
  );
};

// Injects the admin-configured GA4 tag at runtime (must live inside QueryClientProvider).
const GoogleAnalytics = () => {
  useGoogleAnalytics();
  return null;
};

// Route /admin to the correct dashboard for the caller's role.
// Platform admins see the full dashboard; studio admins see the scoped studio dashboard.
const AdminDashboardRouter = () => {
  const { hasRole } = useAuth();
  if (hasRole("admin")) return <AdminDashboard />;
  if (hasRole("studio_admin")) return <StudioAdminDashboard />;
  return <Navigate to="/dashboard" replace />;
};

// Restrict /delivery to platform admins only.
const AdminOnlyRoute = ({ children }: { children: JSX.Element }) => {
  const { hasRole } = useAuth();
  return hasRole("admin") ? children : <Navigate to="/dashboard" replace />;
};

// App component with all providers
// Forwards /signup?<query> → /auth?tab=signup&<query>, preserving params like ?ref=CODE
const SignupRedirect = () => {
  const [params] = useSearchParams();
  const merged = new URLSearchParams(params);
  if (!merged.has("tab")) merged.set("tab", "signup");
  return <Navigate to={`/auth?${merged.toString()}`} replace />;
};

// Forwards a retired path to its current one, preserving the query string
// (plain <Navigate to="/x"> drops it) so old deep links like
// /admin/bug?tix=1042 still resolve.
const LegacyPathRedirect = ({ to }: { to: string }) => {
  const [params] = useSearchParams();
  const search = params.toString();
  return <Navigate to={search ? `${to}?${search}` : to} replace />;
};


const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <ShowsProvider>
          <EpisodesProvider>
            <Toaster />
            <Sonner />
            <ProductionPreviewIndicator />
            <VersionUpdatePrompt />
            <StagingModeBanner />
          <BrowserRouter>
          <ImpersonationBanner />
          <RouteTooltips />

          <GoogleAnalytics />
          <SeoManager />
          <RecoveryRedirect />
          <BugReportProvider>
          <Suspense fallback={null}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/login" element={<Navigate to="/auth" replace />} />
            <Route path="/signup" element={<SignupRedirect />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/session-expired" element={<SessionExpired />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/dmca" element={<DMCA />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/gdpr" element={<GDPRPage />} />

            {/* Onboarding Routes (require auth but not full onboarding) */}
            <Route path="/onboarding/plan" element={<PlanSelection />} />
            <Route path="/onboarding/team" element={<TeamInvite />} />

            {/* Team Invitation Acceptance Route (token-based) */}
            <Route path="/invite/:token" element={<AcceptInvite />} />
            {/* Admin/Network-Admin Invitation Redemption (mints fresh auth token on click) */}
            <Route path="/invitation/:invitationId" element={<RedeemInvite />} />

            {/* DocuSign embedded signing return bridge */}
            <Route path="/contract-return" element={<ContractReturn />} />

            {/* Public Show Page */}
            <Route path="/podcast/:showSlug" element={<PublicShowPage />} />
            <Route path="/s/:slug" element={<SmartLinkRedirect />} />
            <Route path="/help" element={<PublicHelp />} />
            <Route path="/blog" element={<BlogFeed />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/studios" element={<Studios />} />
            <Route path="/studios/:locationSlug" element={<StudioLocationPage />} />
            <Route path="/studios/:locationSlug/:roomSlug" element={<StudioRoomPage />} />
            <Route path="/studios/reservation/:id" element={<StudioReservationThankYou />} />
            <Route path="/studio-booking-action" element={<StudioBookingActionResult />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/about" element={<About />} />
            <Route path="/why-you-should-join-podgo" element={<WhyJoinPodgo />} />

            {/* TEMP: demo-only onboarding prototype, remove after demo */}
            <Route path="/onboarding-demo" element={<OnboardingDemo />} />

            {/* Clip Studio (beta) — admins + individually enabled users; gated in the page */}
            <Route element={
              <ProtectedRoute>
                <PodcasterLayout />
              </ProtectedRoute>
            }>
              <Route path="/clip-studio" element={<ClipStudio page="clips" />} />
              <Route path="/clip-studio/social-accounts" element={<ClipStudio page="connect" />} />
              <Route path="/clip-studio/compose" element={<ClipStudio page="compose" />} />
              <Route path="/clip-studio/smart-links" element={<ClipStudio page="links" />} />
              <Route path="/clip-studio/performance" element={<ClipStudio page="clipstats" />} />
              <Route path="/voice-studio" element={<VoiceStudio page="studio" />} />
              <Route path="/voice-studio/episodes" element={<VoiceStudio page="episodes" />} />
              <Route path="/voice-studio/voices" element={<VoiceStudio page="voices" />} />
            </Route>

            {/* Account-level routes — reachable without a plan so users can still
                manage their profile and subscribe from Billing. */}
            <Route element={
              <ProtectedRoute requirePlan={false}>
                <PodcasterLayout />
              </ProtectedRoute>
            }>
              <Route path="/billing" element={<Billing />} />
              <Route path="/settings" element={<Settings />} />
            </Route>

            {/* Protected Podcaster Portal Routes */}
            <Route element={
              <ProtectedRoute>
                <PodcasterLayout />
              </ProtectedRoute>
            }>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/shows" element={<Shows />} />
              <Route path="/shows/:showId" element={<ShowDetail />} />
              <Route path="/episodes" element={<Episodes />} />
              <Route path="/episodes/:episodeId" element={<EpisodeDetail />} />
              <Route path="/episodes/upload" element={<EpisodeUpload />} />
              {/* Shareable, reload-safe wizard step URLs */}
              <Route path="/episodes/upload/:draftId/step/:stepNum" element={<EpisodeUpload />} />

              <Route path="/delivery" element={<PlatformAdminRoute><DeliveryStatus /></PlatformAdminRoute>} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/analytics-v2" element={<AnalyticsV2 />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/wallet/stripe-return" element={<StripeConnectReturn />} />
              <Route path="/revenue" element={<Revenue />} />
              <Route path="/earnings" element={<Earnings />} />
              <Route path="/revenue-split" element={<RevenueSplit />} />
              <Route path="/team" element={<Team />} />
              
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/book-studio" element={<BookStudio />} />
              <Route path="/my-bookings" element={<MyBookings />} />
              <Route path="/my-bookings/:id" element={<MyBookingDetail />} />
            </Route>

            {/* Admin Routes — outer guard allows platform admin OR network admin.
                Platform-only routes are individually wrapped in <PlatformAdminRoute>. */}
            <Route element={
              <AnyAdminRoute>
                <AdminLayout />
              </AnyAdminRoute>
            }>
              <Route path="/admin" element={
                <AnyAdminRoute>
                  <AdminDashboardRouter />
                </AnyAdminRoute>
              } />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/users/:id" element={<AdminUserDetail />} />
              <Route path="/admin/podcasts" element={<AdminPodcasts />} />
              <Route path="/admin/podcasts/:showId" element={<ShowDetail />} />
              <Route path="/admin/approval-queue" element={<ApprovalQueue />} />
              <Route path="/admin/approval-queue/:queueId" element={<ApprovalReview />} />
              <Route path="/admin/network-admins" element={<PlatformAdminRoute><AdminNetworkAdmins /></PlatformAdminRoute>} />
              <Route path="/admin/trashed-episodes" element={<PlatformAdminRoute><AdminTrashedEpisodes /></PlatformAdminRoute>} />
              <Route path="/admin/analytics" element={<AnyAdminRoute><AdminPlatformAnalytics /></AnyAdminRoute>} />
              <Route path="/admin/insights/platform-revenue" element={<PlatformAdminRoute><AdminPlatformRevenue /></PlatformAdminRoute>} />
              <Route path="/admin/support" element={<PlatformAdminRoute><AdminSupport /></PlatformAdminRoute>} />
              <Route path="/admin/templates" element={<PlatformAdminRoute><Templates /></PlatformAdminRoute>} />
              <Route path="/admin/email-templates" element={<Navigate to="/admin/templates?tab=email" replace />} />
              <Route path="/admin/sms-templates" element={<Navigate to="/admin/templates?tab=sms" replace />} />
              <Route path="/admin/segments" element={<PlatformAdminRoute><AdminSegments /></PlatformAdminRoute>} />
              <Route path="/admin/campaigns" element={<PlatformAdminRoute><AdminCampaigns /></PlatformAdminRoute>} />
              <Route path="/admin/campaigns/:id" element={<PlatformAdminRoute><AdminCampaignEditor /></PlatformAdminRoute>} />
              <Route path="/admin/campaigns/:id/stats" element={<PlatformAdminRoute><AdminCampaignStats /></PlatformAdminRoute>} />
              <Route path="/admin/settings" element={<PlatformAdminRoute><AdminSettings /></PlatformAdminRoute>} />
              <Route path="/admin/expenses" element={<PlatformAdminRoute><AdminExpenses /></PlatformAdminRoute>} />
              <Route path="/admin/revenue-entries" element={<PlatformAdminRoute><AdminRevenueEntries /></PlatformAdminRoute>} />
              <Route path="/admin/withdrawals" element={<PlatformAdminRoute><AdminWithdrawals /></PlatformAdminRoute>} />
              <Route path="/admin/articles" element={<PlatformAdminRoute><AdminNews /></PlatformAdminRoute>} />
              <Route path="/admin/articles/new" element={<PlatformAdminRoute><AdminArticleEdit /></PlatformAdminRoute>} />
              <Route path="/admin/articles/:id/edit" element={<PlatformAdminRoute><AdminArticleEdit /></PlatformAdminRoute>} />
              <Route path="/admin/studios" element={<StudioAdminRoute><AdminStudios /></StudioAdminRoute>} />
              <Route path="/admin/studios/:locationId" element={<StudioAdminRoute><StudioLocationDetail /></StudioAdminRoute>} />
              <Route path="/admin/studios/:locationId/rooms/:roomId" element={<StudioAdminRoute><StudioRoomDetail /></StudioAdminRoute>} />
              <Route path="/admin/studio-bookings" element={<StudioAdminRoute><StudioBookings /></StudioAdminRoute>} />
              <Route path="/admin/studio-bookings/:bookingId" element={<StudioAdminRoute><StudioBookingDetail /></StudioAdminRoute>} />
              <Route path="/admin/careers" element={<PlatformAdminRoute><AdminCareers /></PlatformAdminRoute>} />
              <Route path="/admin/careers/new" element={<PlatformAdminRoute><JobPostingEdit /></PlatformAdminRoute>} />
              <Route path="/admin/careers/:id/edit" element={<PlatformAdminRoute><JobPostingEdit /></PlatformAdminRoute>} />
              <Route path="/admin/job-applications" element={<PlatformAdminRoute><JobApplications /></PlatformAdminRoute>} />
              <Route path="/admin/bugs" element={<PlatformAdminRoute><AdminBugReports /></PlatformAdminRoute>} />
              {/* Retired paths from earlier notification emails */}
              <Route path="/admin/bug" element={<LegacyPathRedirect to="/admin/bugs" />} />
              <Route path="/admin/bug-reports" element={<LegacyPathRedirect to="/admin/bugs" />} />
              <Route path="/admin/demo-links" element={<LegacyPathRedirect to="/admin/link-hub" />} />

              <Route path="/admin/sync-exclusions" element={<PlatformAdminRoute><AdminSyncExclusions /></PlatformAdminRoute>} />
              <Route path="/admin/failed-syncs" element={<PlatformAdminRoute><AdminFailedSyncs /></PlatformAdminRoute>} />
              <Route path="/admin/link-hub" element={<PlatformAdminRoute><AdminLinkHub /></PlatformAdminRoute>} />
              <Route path="/admin/theme" element={<PlatformAdminRoute><AdminThemeOptions /></PlatformAdminRoute>} />
              <Route path="/admin/scheduling" element={<SchedulingAdminRoute><SchedulingOverview /></SchedulingAdminRoute>} />
              <Route path="/admin/scheduling/groups" element={<SchedulingAdminRoute><SchedulingGroups /></SchedulingAdminRoute>} />
              <Route path="/admin/scheduling/booking-types" element={<SchedulingAdminRoute><SchedulingBookingTypes /></SchedulingAdminRoute>} />
              <Route path="/admin/scheduling/my-availability" element={<SchedulingSelfRoute><MyAvailability /></SchedulingSelfRoute>} />
              
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </BugReportProvider>
          </BrowserRouter>
          </EpisodesProvider>
        </ShowsProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
